import { useState } from 'react';
import {
  compareJalali, formatJalaliFull, monthLength, MONTH_NAMES, todayJalali, WEEKDAY_SHORT, weekdayIndex,
  type JalaliDate,
} from '@/core/format/jalali';
import { toPersianDigits } from '@/core/format/numbers';
import { cn } from './cn';
import { Icon } from './Icon';
import { Sheet } from './Sheet';

interface Props {
  open: boolean;
  onClose: () => void;
  value: JalaliDate | null;
  onChange: (d: JalaliDate) => void;
  min?: JalaliDate;
  max?: JalaliDate;
  title?: string;
}

export function JalaliDatePicker({ open, onClose, value, onChange, min, max, title = 'انتخاب تاریخ' }: Props) {
  const initial = value ?? todayJalali();
  const [view, setView] = useState({ year: initial.year, month: initial.month });
  const [yearMode, setYearMode] = useState(false);

  const shift = (delta: number) =>
    setView(({ year, month }) => {
      const m = month + delta;
      if (m < 1) return { year: year - 1, month: 12 };
      if (m > 12) return { year: year + 1, month: 1 };
      return { year, month: m };
    });

  const first = weekdayIndex({ ...view, day: 1 });
  const days = monthLength(view.year, view.month);
  const today = todayJalali();
  const disabled = (d: JalaliDate) => (min && compareJalali(d, min) < 0) || (max && compareJalali(d, max) > 0);

  const minYear = min?.year ?? view.year - 100;
  const maxYear = max?.year ?? view.year + 1;
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      <div className="mb-3 flex items-center justify-between">
        <button type="button" onClick={() => shift(-1)} aria-label="ماه قبل" className="grid size-10 place-items-center rounded-full hover:bg-fill">
          <Icon name="back" />
        </button>
        <button type="button" onClick={() => setYearMode((v) => !v)} className="flex items-center gap-1 rounded-md px-3 py-1.5 font-bold hover:bg-fill" aria-expanded={yearMode}>
          {MONTH_NAMES[view.month - 1]} {toPersianDigits(view.year)}
          <Icon name="chevronDown" size={16} />
        </button>
        <button type="button" onClick={() => shift(1)} aria-label="ماه بعد" className="grid size-10 place-items-center rounded-full hover:bg-fill">
          <Icon name="forward" />
        </button>
      </div>

      {yearMode ? (
        <div className="grid max-h-72 grid-cols-4 gap-2 overflow-y-auto" role="listbox" aria-label="سال">
          {years.map((y) => (
            <button key={y} type="button" role="option" aria-selected={y === view.year}
              onClick={() => { setView((v) => ({ ...v, year: y })); setYearMode(false); }}
              className={cn('rounded-md py-2 text-sm font-medium', y === view.year ? 'bg-solid text-white' : 'hover:bg-fill')}>
              {toPersianDigits(y)}
            </button>
          ))}
        </div>
      ) : (
        <div role="grid" aria-label={`${MONTH_NAMES[view.month - 1]} ${view.year}`}>
          <div className="mb-1 grid grid-cols-7 text-center text-xs font-bold text-ink-3">
            {WEEKDAY_SHORT.map((w) => <span key={w} className="py-1">{w}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-y-1">
            {Array.from({ length: first }, (_, i) => <span key={`b${i}`} />)}
            {Array.from({ length: days }, (_, i) => {
              const d: JalaliDate = { ...view, day: i + 1 };
              const isSel = value && compareJalali(d, value) === 0;
              const isToday = compareJalali(d, today) === 0;
              return (
                <button key={d.day} type="button" disabled={Boolean(disabled(d))} aria-pressed={Boolean(isSel)}
                  aria-label={formatJalaliFull(d)} onClick={() => { onChange(d); onClose(); }}
                  className={cn('mx-auto grid size-10 place-items-center rounded-full text-sm font-medium transition',
                    isSel ? 'bg-cta text-white shadow-glow' : 'hover:bg-fill',
                    isToday && !isSel && 'border border-turquoise text-action',
                    'disabled:text-ink-3/50 disabled:hover:bg-transparent')}>
                  {toPersianDigits(d.day)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Sheet>
  );
}
